import { format } from 'date-fns';
import {
  DateSearchSelect,
  InputSearch,
  MultipleSearchSelect
} from '@/components/custom/erp/data-table-v2/search-inputs';
import { buildDataTableCols, type DataTableCol } from '@/components/custom/erp/data-table-v2/column-builder';
import { buildInputTableCols, type InputTableCol } from '@/components/custom/erp/input-table-v2';
import { formatDate, formatMoney, formatNumber, formatRoundedNumber } from '@/lib/formatUtils';
import { ConversionQuantityFieldV2, DvtFieldV2 } from '@/components/custom/erp/dvt-field-v2';
import type { DataTableColumn } from '@/components/custom/erp/data-table-v2/types';

type DonHangMuaTrongNuocRow = MuaHangRecord;

const STATUS_OPTIONS = [
  { value: '0', label: 'Lập chứng từ' },
  { value: '1', label: 'Chờ duyệt' },
  { value: '4', label: 'Đang duyệt' },
  { value: '5', label: 'Đã duyệt' },
  { value: '6', label: 'Đang thực hiện' },
  { value: '7', label: 'Hoàn thành' },
  { value: '9', label: 'Đóng' }
];

export const getDataTableColumns = (
  handleViewClick: (row: MuaHangRecord) => void,
  totalAmount?: number | null,
  isTotalLoading?: boolean
): DataTableColumn[] => {
  const total = (
    <div className='flex items-center gap-2'>
      <span className='text-lg font-bold'>&#931;</span>
      <span className='font-semibold text-main'>
        {isTotalLoading ? 'Đang tính...' : typeof totalAmount === 'number' ? formatRoundedNumber(totalAmount) : '—'}
      </span>
    </div>
  );

  const columns: DataTableCol<DonHangMuaTrongNuocRow>[] = [
    {
      field: 'status',
      headerName: 'Trạng thái',
      width: 120,
      renderCell: params => {
        const option = STATUS_OPTIONS.find(opt => opt.value === params.value);
        return option?.label || '';
      },
      renderSearchInput: params => <MultipleSearchSelect {...params} options={STATUS_OPTIONS} />
    },
    {
      field: 'so_ct',
      headerName: 'Số c/từ',
      width: 150,
      renderCell: params => (
        <button
          onClick={e => {
            e.stopPropagation();
            handleViewClick(params.row);
          }}
          className='hover:text-blue-500 hover:underline'
        >
          {String(params.value ?? '')}
        </button>
      )
    },
    {
      field: 'ngay_ct',
      headerName: 'Ngày c/từ',
      width: 120,
      renderCell: params => formatDate(params.row.ngay_ct),
      renderSearchInput: params => <DateSearchSelect {...params} />
    },
    {
      field: 'ma_kh',
      headerName: 'Mã nhà cung cấp',
      width: 120,
      renderCell: params => params.row.ma_kh_data?.ma_kh || params.row.ma_ncc_data?.ma_kh
    },
    {
      field: 'ten_kh',
      headerName: 'Tên nhà cung cấp',
      width: 200,
      renderCell: params => params.row.ma_kh_data?.ten_kh || params.row.ma_ncc_data?.ten_kh
    },
    {
      field: 'dien_giai',
      headerName: 'Diễn giải',
      width: 200
    },
    {
      field: 't_tt_nt',
      headerName: 'Tổng tiền',
      width: 150,
      columnType: 'number',
      align: 'right',
      renderCell: params => formatRoundedNumber(params.row.t_tt_nt ?? params.row.t_tt),
      renderSearchInput: params => <InputSearch {...params} after={total} />
    },
    {
      field: 'ma_nt',
      headerName: 'Mã ngoại tệ',
      width: 120,
      renderCell: params => params.row.ma_nt_data?.ma_nt
    },
    {
      field: 'ma_ngv',
      headerName: 'Loại chứng từ',
      width: 120,
      renderCell: () => '5. Đơn hàng'
    },
    {
      field: 'username0',
      headerName: 'Người tạo',
      width: 150,
      renderCell: params => params.row.user_id0_data?.username
    },
    {
      field: 'datetime0',
      headerName: 'Ngày tạo',
      width: 150,
      renderCell: params => {
        return format(params.row.created, 'dd/MM/yyyy HH:mm:ss');
      }
    },
    {
      field: 'username2',
      headerName: 'Người sửa',
      width: 150,
      renderCell: params => params.row.user_id2_data?.username
    },
    {
      field: 'datetime2',
      headerName: 'Ngày sửa',
      width: 150,
      renderCell: params => {
        if (!params.row.updated) return '';
        return format(params.row.updated, 'dd/MM/yyyy HH:mm:ss');
      }
    }
  ];

  return buildDataTableCols(columns);
};

export const getInputTableColumns = (data?: MuaHangRecord[]): DataTableColumn[] => {
  const hasDataForField = (fieldPath: string): boolean => {
    if (!data || data.length === 0) return false;

    return data.some(row => {
      const fieldParts = fieldPath.split('.');
      let value = row;

      for (const part of fieldParts) {
        value = value?.[part];
        if (value === undefined || value === null) return false;
      }

      return value !== '' && value != 0;
    });
  };

  const columns: Array<InputTableCol<DonHangMuaTrongNuocRow> | false> = [
    {
      field: 'ma_vt',
      headerName: 'Mã sản phẩm',
      width: 120,
      renderCell: params => params.row.ma_vt_data?.ma_vt
    },
    {
      field: 'ten_vt',
      headerName: 'Tên sản phẩm',
      width: 200,
      renderCell: params => params.row.ma_vt_data?.ten_vt
    },
    {
      field: 'dvt',
      headerName: 'Đvt',
      width: 100,
      renderCell: params => <DvtFieldV2 row={params.row} />
    },

    hasDataForField('ma_kho_data.ma_kho') && {
      field: 'ma_kho',
      headerName: 'Mã kho',
      width: 150,
      renderCell: params => params.row.ma_kho_data?.ma_kho
    },
    {
      field: 'so_luong',
      headerName: 'Số lượng',
      width: 100,
      renderCell: params => <ConversionQuantityFieldV2 row={params.row} />
    },

    hasDataForField('ct_km') && {
      field: 'ct_km',
      headerName: 'Loại hàng',
      width: 120,
      renderCell: params => {
        const ctKm = params.row.ct_km;
        switch (ctKm) {
          case false:
            return '0. Hàng bán';
          case true:
            return '1. Hàng KM';
        }
      }
    },

    hasDataForField('gia_nt2') && {
      field: 'gia_nt2',
      headerName: 'Giá bán VND',
      width: 150,
      columnType: 'number',
      renderCell: params => formatMoney(params.row.gia_nt2)
    },

    hasDataForField('tien_nt2') && {
      field: 'tien_nt2',
      headerName: 'Thành tiền VND',
      width: 150,
      columnType: 'number',
      renderCell: params => formatMoney(params.row.tien_nt2)
    },

    hasDataForField('tien_nt') && {
      field: 'tien_nt',
      headerName: 'Tiền VND',
      width: 150,
      columnType: 'number',
      renderCell: params => formatMoney(params.row.tien_nt)
    },

    hasDataForField('tien_tck_nt') && {
      field: 'tien_tck_nt',
      headerName: 'Tiền trước chiết khấu VND',
      width: 180,
      columnType: 'number',
      renderCell: params => formatMoney(params.row.tien_tck_nt)
    },

    hasDataForField('tl_ck') && {
      field: 'tl_ck',
      headerName: 'Tl ck(%)',
      width: 120,
      columnType: 'number',
      renderCell: params => formatNumber(params.row.tl_ck)
    },

    hasDataForField('ck_nt') && {
      field: 'ck_nt',
      headerName: 'Chiết khấu VND',
      width: 150,
      columnType: 'number',
      renderCell: params => formatMoney(params.row.ck_nt)
    },

    hasDataForField('ma_thue') && {
      field: 'ma_thue',
      headerName: 'Thuế suất',
      width: 150,
      columnType: 'number',
      renderCell: params => params.row.ma_thue_data?.ten_thue
    },

    hasDataForField('ma_thue_data') && {
      field: 'thue_suat',
      headerName: 'Thuế suất(%)',
      width: 100,
      columnType: 'number',
      renderCell: params => formatNumber(params.row.ma_thue_data?.thue_suat)
    },

    hasDataForField('ma_bp_data.ma_bp') && {
      field: 'ma_bp',
      headerName: 'Bộ phận',
      width: 150,
      renderCell: params => params.row.ma_bp_data?.ma_bp
    },

    hasDataForField('ma_vv') && {
      field: 'ma_vv',
      headerName: 'Vụ việc',
      width: 150,
      renderCell: params => params.row.ma_vv_data?.ma_vv
    },

    hasDataForField('ma_hd') && {
      field: 'ma_hd',
      headerName: 'Hợp đồng',
      width: 150,
      renderCell: params => params.row.ma_hd_data?.ma_hd
    },

    hasDataForField('ma_dtt') && {
      field: 'ma_dtt',
      headerName: 'Đợt thanh toán',
      width: 150,
      renderCell: params => params.row.ma_dtt_data?.ma_dtt
    },

    hasDataForField('ma_ku') && {
      field: 'ma_ku',
      headerName: 'Khế ước',
      width: 150,
      renderCell: params => params.row.ma_ku_data?.ma_ku
    },

    hasDataForField('ma_phi') && {
      field: 'ma_phi',
      headerName: 'Phí',
      width: 120,
      renderCell: params => params.row.ma_phi_data?.ma_phi
    },

    hasDataForField('ma_sp_data.ma_vt') && {
      field: 'ma_sp',
      headerName: 'Sản phẩm',
      width: 150,
      renderCell: params => params.row.ma_sp_data?.ma_vt
    },

    hasDataForField('ma_cp0') && {
      field: 'ma_cp0',
      headerName: 'C/p không h/lệ',
      width: 150,
      renderCell: params => params.row.ma_cp0_data?.ma_cpkhl
    }
  ];

  return buildInputTableCols(columns.filter(Boolean) as InputTableCol<DonHangMuaTrongNuocRow>[]);
};

export const historyColumns: DataTableColumn[] = buildDataTableCols([
  { field: 'username', headerName: 'Tài khoản', width: 100 },
  { field: 'nickname', headerName: 'Tên tài khoản', width: 200 },
  { field: 'action', headerName: 'Hành động', width: 100 },
  { field: 'datetime0', headerName: 'Thời gian', width: 150 }
]);
